import L from 'leaflet'

/**
 * ItemManager - Manages map items (markers, lines, polygons, circles)
 *
 * Handles CRUD operations for items within layers. Items are stored in layers
 * and rendered as Leaflet elements.
 */
export default class ItemManager {
  /**
   * Create a new ItemManager
   * @param {LayerManager} layerManager - The LayerManager instance
   * @param {Function} [warnFn] - Optional warning function for logging
   */
  constructor(layerManager, warnFn = console.warn) {
    this._layerManager = layerManager
    this._warn = warnFn
  }

  /**
   * Add or update an item on the map
   *
   * @param {Object} item - Item configuration
   * @param {string} item.id - Unique identifier
   * @param {string} item.layer - Layer ID to add item to (must exist)
   * @param {Object} item.geometry - Geometry definition
   * @param {string} item.geometry.type - 'Point', 'LineString', 'Polygon', 'Circle'
   * @param {Array} item.geometry.coordinates - Coordinates array
   * @param {number} [item.geometry.radius] - Circle radius in meters
   * @param {Object} [item.icon] - Icon configuration (for Point geometry)
   * @param {Object} [item.style] - Style for lines/polygons
   * @param {Object} [item.popup] - Popup configuration
   * @param {Function} [item.onClick] - Click handler function
   * @returns {L.Layer|undefined} Leaflet layer object or undefined if failed
   */
  addItem(item) {
    if (!item.id) {
      this._warn('Item must have an id')
      return
    }

    if (!item.layer) {
      this._warn('Item must specify a layer')
      return
    }

    const layer = this._layerManager._getLayer(item.layer)
    if (!layer) {
      this._warn('Layer not found:', item.layer)
      return
    }

    // Remove existing if updating
    if (layer.items[item.id]) {
      this.removeItem(item.id)
    }

    // Create Leaflet element based on geometry type
    let leafletElement

    switch (item.geometry.type) {
      case 'Point':
        leafletElement = this._createPoint(item)
        break
      case 'LineString':
        leafletElement = this._createLineString(item)
        break
      case 'Polygon':
        leafletElement = this._createPolygon(item)
        break
      case 'Circle':
        leafletElement = this._createCircle(item)
        break
      default:
        this._warn('Unsupported geometry type:', item.geometry.type)
        return
    }

    if (!leafletElement) return

    // Add to layer's group
    leafletElement.addTo(layer.group)

    // Store reference
    layer.items[item.id] = {
      data: item,
      element: leafletElement,
    }

    return leafletElement
  }

  /**
   * Create a Point marker
   * @private
   */
  _createPoint(item) {
    const [lat, lng] = item.geometry.coordinates

    const icon = L.divIcon({
      html: item.icon?.html || '<div>●</div>',
      iconSize: item.icon?.size || [24, 24],
      iconAnchor: item.icon?.anchor || [12, 12],
      className: item.icon?.classname || '',
    })

    const marker = L.marker([lat, lng], {
      icon,
      pane: `layer-${item.layer}`,
    })

    // Add popup if provided
    if (item.popup?.content) {
      marker.bindPopup(item.popup.content)
    }

    // Add click handler if provided
    if (item.onClick && typeof item.onClick === 'function') {
      marker.on('click', () => item.onClick(item.id))
    }

    return marker
  }

  /**
   * Create a LineString polyline
   * @private
   */
  _createLineString(item) {
    const coords = item.geometry.coordinates.map(c => [c[0], c[1]])

    const line = L.polyline(coords, {
      color: item.style?.color || '#3388ff',
      weight: item.style?.weight || 3,
      opacity: item.style?.opacity || 1,
      pane: `layer-${item.layer}`,
    })

    if (item.popup?.content) {
      line.bindPopup(item.popup.content)
    }

    return line
  }

  /**
   * Create a Polygon shape
   * @private
   */
  _createPolygon(item) {
    const coords = item.geometry.coordinates.map(c => [c[0], c[1]])

    const polygon = L.polygon(coords, {
      color: item.style?.color || '#3388ff',
      weight: item.style?.weight || 3,
      fillColor: item.style?.fillcolor || '#3388ff',
      fillOpacity: item.style?.fillopacity || 0.2,
      pane: `layer-${item.layer}`,
    })

    if (item.popup?.content) {
      polygon.bindPopup(item.popup.content)
    }

    return polygon
  }

  /**
   * Create a Circle shape
   * @private
   */
  _createCircle(item) {
    const [lat, lng] = item.geometry.coordinates
    const radius = item.geometry.radius || 100 // meters

    const circle = L.circle([lat, lng], {
      radius,
      color: item.style?.color || '#3388ff',
      weight: item.style?.weight || 3,
      fillColor: item.style?.fillcolor || '#3388ff',
      fillOpacity: item.style?.fillopacity || 0.2,
      pane: `layer-${item.layer}`,
    })

    if (item.popup?.content) {
      circle.bindPopup(item.popup.content)
    }

    return circle
  }

  /**
   * Remove an item from the map
   *
   * @param {string} id - Item identifier
   */
  removeItem(id) {
    const layers = this._layerManager._getAllLayers()
    for (const layerId in layers) {
      const layer = layers[layerId]
      if (layer.items[id]) {
        layer.group.removeLayer(layer.items[id].element)
        delete layer.items[id]
        return
      }
    }
  }

  /**
   * Update an existing item
   *
   * @param {string} id - Item identifier
   * @param {Object} updates - Properties to update (merged with existing data)
   */
  updateItem(id, updates) {
    const layers = this._layerManager._getAllLayers()
    for (const layerId in layers) {
      const layer = layers[layerId]
      if (layer.items[id]) {
        const merged = Object.assign({}, layer.items[id].data, updates)
        this.addItem(merged)
        return
      }
    }
    this._warn('Item not found:', id)
  }

  /**
   * Get item data by ID
   *
   * @param {string} id - Item identifier
   * @returns {Object|null} Item data or null if not found
   */
  getItem(id) {
    const layers = this._layerManager._getAllLayers()
    for (const layerId in layers) {
      const layer = layers[layerId]
      if (layer.items[id]) {
        return layer.items[id].data
      }
    }
    return null
  }

  /**
   * Get the Leaflet element for an item
   *
   * @param {string} id - Item identifier
   * @returns {L.Layer|null} Leaflet element or null if not found
   */
  getItemElement(id) {
    const layers = this._layerManager._getAllLayers()
    for (const layerId in layers) {
      const layer = layers[layerId]
      if (layer.items[id]) {
        return layer.items[id].element
      }
    }
    return null
  }

  /**
   * Clear all items from a specific layer
   *
   * @param {string} layerId - Layer identifier
   */
  clearLayer(layerId) {
    const layer = this._layerManager._getLayer(layerId)
    if (!layer) {
      this._warn('Layer not found:', layerId)
      return
    }

    layer.group.clearLayers()
    layer.items = {}
  }

  /**
   * Clear all items from all layers
   */
  clearAllItems() {
    const layers = this._layerManager._getAllLayers()
    for (const layerId in layers) {
      this.clearLayer(layerId)
    }
  }
}
